import type { FormxFieldEntity, FormxFieldMetadatas, FormxFieldType, FormxFormEntity, FormxSubmissionEntity, FormxValueEntity } from '@/features/formx/domain/entities/FormxEntity.js';
import type { FieldCreateInput, FieldUpdateInput, FormCreateInput, FormListOptions, FormUpdateInput, FormxFieldValueWithSubmission, FormxKpiFieldItem, FormxKpiValueItem, FormxPhotoItem, IFormxRepository, ReorderInput, SubmissionCreateInput, SubmissionListOptions, SubmissionUpdateInput } from '@/features/formx/domain/repositories/IFormxRepository.js';
import { FormxRemoteDataSource } from '@/features/formx/data/datasources/FormxRemoteDataSource.js';
import type { FormxFieldDto, FormxFormDto, FormxSubmissionDto, FormxValueDto } from '@/features/formx/data/dtos/FormxDto.js';

function mapForm(d: FormxFormDto): FormxFormEntity {
  return { id: d.id, enabled: d.enabled ?? true, orgId: d.org_id, projectId: d.project_id, name: d.name, description: d.description ?? null, submissionLimit: d.submission_limit ?? 0, sortOrder: d.sort_order ?? 0, createdAt: d.created_at, updatedAt: d.updated_at };
}

function mapField(d: FormxFieldDto): FormxFieldEntity {
  return {
    id: d.id, enabled: d.enabled ?? true, formId: d.form_id, orgId: d.org_id, code: d.code, label: d.label,
    hint: d.hint ?? null, fieldType: (d.field_type as FormxFieldType) ?? 'text',
    isRequired: d.is_required, defaultValue: d.default_value ?? null,
    target: d.target ?? null, targetUnit: d.target_unit ?? null,
    metadatas: (d.metadatas as FormxFieldMetadatas | null) ?? null,
    sortOrder: d.sort_order ?? 0, createdAt: d.created_at, updatedAt: d.updated_at,
  };
}

function mapSubmission(d: FormxSubmissionDto): FormxSubmissionEntity {
  return {
    id: d.id, formId: d.form_id, orgId: d.org_id,
    sessionId: d.session_id ?? null, locationId: d.location_id ?? null,
    locationName: d.location?.name ?? null,
    locationProvinceName: d.location?.province?.full_name ?? d.location?.province?.name ?? null,
    createdBy: d.created_by, notes: d.notes ?? null,
    submittedAt: d.submitted_at ?? null, createdAt: d.created_at,
  };
}

function mapValue(d: FormxValueDto): FormxValueEntity {
  return { id: d.id, submissionId: d.submission_id, fieldId: d.field_id, value: d.value, createdAt: d.created_at };
}

export class FormxRepository implements IFormxRepository {
  constructor(private readonly ds: FormxRemoteDataSource) {}

  async listForms(options: FormListOptions): Promise<{ data: FormxFormEntity[]; total: number }> {
    const { data, total } = await this.ds.listForms(options);
    return { data: data.map(mapForm), total };
  }

  async getForm(id: string): Promise<FormxFormEntity | null> {
    const d = await this.ds.getForm(id);
    return d ? mapForm(d) : null;
  }

  async createForm(input: FormCreateInput): Promise<FormxFormEntity> {
    const d = await this.ds.createForm({ org_id: input.orgId, project_id: input.projectId, name: input.name, description: input.description ?? null, submission_limit: input.submissionLimit ?? 0, enabled: input.enabled ?? true });
    if (!d) throw new Error('Form creation failed');
    return mapForm(d);
  }

  async updateForm(id: string, input: FormUpdateInput): Promise<FormxFormEntity | null> {
    const p: Partial<FormxFormDto> = {};
    if (input.name !== undefined) p.name = input.name;
    if (input.description !== undefined) p.description = input.description;
    if (input.submissionLimit !== undefined) p.submission_limit = input.submissionLimit;
    if (input.enabled !== undefined) p.enabled = input.enabled;
    if (input.sortOrder !== undefined) p.sort_order = input.sortOrder;
    const d = await this.ds.updateForm(id, p);
    return d ? mapForm(d) : null;
  }

  async deleteForm(id: string): Promise<void> {
    await this.ds.deleteForm(id);
  }

  async reorderForms(updates: ReorderInput): Promise<void> {
    await this.ds.reorderForms(updates);
  }

  async listFields(formId: string): Promise<FormxFieldEntity[]> {
    return (await this.ds.listFields(formId)).map(mapField);
  }

  async createField(input: FieldCreateInput): Promise<FormxFieldEntity> {
    const d = await this.ds.createField({
      form_id: input.formId, org_id: input.orgId, code: input.code!, label: input.label,
      hint: input.hint ?? null, field_type: input.fieldType ?? 'text',
      is_required: input.isRequired ?? true, default_value: input.defaultValue ?? null,
      target: input.target ?? null, target_unit: input.targetUnit ?? null,
      metadatas: input.metadatas ?? null,
      sort_order: input.sortOrder ?? 0, enabled: input.enabled ?? true,
    });
    if (!d) throw new Error('Field creation failed');
    return mapField(d);
  }

  async updateField(id: string, input: FieldUpdateInput): Promise<FormxFieldEntity | null> {
    const p: Partial<FormxFieldDto> = {};
    if (input.code !== undefined) p.code = input.code;
    if (input.label !== undefined) p.label = input.label;
    if (input.hint !== undefined) p.hint = input.hint;
    if (input.fieldType !== undefined) p.field_type = input.fieldType;
    if (input.isRequired !== undefined) p.is_required = input.isRequired;
    if (input.defaultValue !== undefined) p.default_value = input.defaultValue;
    if (input.target !== undefined) p.target = input.target;
    if (input.targetUnit !== undefined) p.target_unit = input.targetUnit;
    if (input.metadatas !== undefined) p.metadatas = input.metadatas;
    if (input.sortOrder !== undefined) p.sort_order = input.sortOrder;
    if (input.enabled !== undefined) p.enabled = input.enabled;
    const d = await this.ds.updateField(id, p);
    return d ? mapField(d) : null;
  }

  async deleteField(id: string): Promise<void> {
    await this.ds.deleteField(id);
  }

  async reorderFields(updates: ReorderInput): Promise<void> {
    await this.ds.reorderFields(updates);
  }

  async listSubmissions(options: SubmissionListOptions): Promise<FormxSubmissionEntity[]> {
    return (await this.ds.listSubmissions(options)).map(mapSubmission);
  }

  async getSubmissionWithValues(id: string): Promise<{ submission: FormxSubmissionEntity; values: FormxValueEntity[] } | null> {
    const result = await this.ds.getSubmissionWithValues(id);
    if (!result) return null;
    return { submission: mapSubmission(result.submission), values: result.values.map(mapValue) };
  }

  async listValueSummaryByForm(formId: string): Promise<Array<{ submissionId: string; fieldId: string }>> {
    const rows = await this.ds.listValueSummaryByForm(formId);
    return rows.map((r) => ({ submissionId: r.submission_id, fieldId: r.field_id }));
  }

  async createSubmissionWithValues(input: SubmissionCreateInput): Promise<string> {
    return this.ds.createSubmissionWithValues(input);
  }

  async updateSubmissionWithValues(id: string, input: SubmissionUpdateInput): Promise<void> {
    return this.ds.updateSubmissionWithValues(id, input);
  }

  async listValuesBySubmissionIds(submissionIds: string[]): Promise<FormxValueEntity[]> {
    return (await this.ds.listValuesBySubmissionIds(submissionIds)).map(mapValue);
  }

  async listValuesByField(fieldId: string, limit?: number): Promise<FormxFieldValueWithSubmission[]> {
    const rows = await this.ds.listValuesByField(fieldId, limit);
    return rows.map((r) => ({
      value: mapValue({ id: r.id, submission_id: r.submission_id, field_id: r.field_id, value: r.value, created_at: r.created_at }),
      submission: mapSubmission(r.submission),
    }));
  }

  async countSubmissionsByProject(projectId: string): Promise<number> {
    return this.ds.countSubmissionsByProject(projectId);
  }

  async listKpiFieldsByProject(projectId: string): Promise<FormxKpiFieldItem[]> {
    const rows = await this.ds.listKpiFieldsByProject(projectId);
    return rows.map((r) => ({ ...mapField(r), formName: r.formName }));
  }

  async listKpiValuesByProject(projectId: string): Promise<FormxKpiValueItem[]> {
    const rows = await this.ds.listKpiValuesByProject(projectId);
    return rows.map((r) => ({
      id: r.id,
      fieldId: r.field_id,
      fieldLabel: r.fieldLabel,
      formId: r.formId,
      formName: r.formName,
      target: r.target,
      targetUnit: r.targetUnit,
      value: r.value,
      submission: mapSubmission(r.submission),
    }));
  }

  async listPhotosByProject(projectId: string, limit?: number, offset?: number): Promise<FormxPhotoItem[]> {
    const rows = await this.ds.listPhotoValuesByProject(projectId, limit, offset);
    return rows.map((r) => ({
      valueId: r.id,
      fieldId: r.field_id,
      submissionId: r.submission_id,
      value: r.value,
      fieldLabel: r.fieldLabel,
      formName: r.formName,
      submission: mapSubmission(r.submission),
    }));
  }
}
